import {useAssistantRuntime} from "@assistant-ui/react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type {ChartWidget} from "@/lib/chat-runtime";
import {FavoriteButton} from "@/components/ui/favorite-button";

interface ChartCardProps {
  chart: ChartWidget;
  /** If true, shows the widget as already favorited (filled star) */
  isFavorited?: boolean;
  /** Callback when favorite button is clicked. If not provided, favorite button is hidden. */
  onFavoriteClick?: () => void;
}

function useOptionalAssistantRuntime() {
  try {
    return useAssistantRuntime();
  } catch {
    return null;
  }
}

export function ChartCard({chart, isFavorited = false, onFavoriteClick}: ChartCardProps) {
  const assistantRuntime = useOptionalAssistantRuntime();

  const colors = chart.colors || {
    positive: "#22c55e",
    negative: "#ef4444",
    neutral: "#a1a1aa",
  };

  const hasStackedData = chart.data.some(
    (d) => d.positive !== undefined || d.negative !== undefined || d.neutral !== undefined
  );

  // Extract time range from title (e.g. "Sentiment over last 24 hours" -> "last 24 hours")
  const timeRangeMatch = chart.title.match(/last \d+ \w+/i);
  const timeRange = timeRangeMatch ? timeRangeMatch[0] : "the selected period";

  // Transform stacked data to pie chart format
  const pieData = hasStackedData
    ? [
        {
          name: "Positive",
          value: chart.data.reduce((sum, d) => sum + (d.positive || 0), 0),
          sentiment: "positive",
        },
        {
          name: "Neutral",
          value: chart.data.reduce((sum, d) => sum + (d.neutral || 0), 0),
          sentiment: "neutral",
        },
        {
          name: "Negative",
          value: chart.data.reduce((sum, d) => sum + (d.negative || 0), 0),
          sentiment: "negative",
        },
      ].filter((d) => d.value > 0)
    : [];

  const handlePieClick = (sentiment: string) => {
    if (!assistantRuntime) return;
    const query = `List topics with ${sentiment} sentiment during ${timeRange}`;
    assistantRuntime.thread.append({
      role: "user",
      content: [{type: "text", text: query}],
    });
  };

  const isInteractive = Boolean(assistantRuntime);

  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900/50">
      <div className="mb-3 min-w-0 flex-1">
        <div className="flex items-center gap-1">
          {onFavoriteClick && (
            <FavoriteButton isFavorited={isFavorited} onClick={onFavoriteClick} />
          )}
          <h3 className="truncate text-sm font-semibold text-zinc-900 dark:text-zinc-100">
            {chart.title}
          </h3>
        </div>
        {chart.description && (
          <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">{chart.description}</p>
        )}
      </div>

      {/* Show pie chart + bar chart side by side for sentiment data */}
      {chart.chart_type === "stacked_bar" && hasStackedData && pieData.length > 0 ? (
        <div className="flex gap-4">
          {/* Pie chart - clickable when in chat context */}
          <div className="h-56 w-full sm:w-1/3">
            {isInteractive && (
              <p className="mb-1 text-center text-xs text-zinc-500 dark:text-zinc-400">
                Click segment to explore
              </p>
            )}
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={70}
                  paddingAngle={2}
                  dataKey="value"
                  cursor={isInteractive ? "pointer" : "default"}
                  onClick={isInteractive ? (data) => handlePieClick(data.sentiment) : undefined}
                >
                  {pieData.map((entry) => (
                    <Cell
                      key={entry.sentiment}
                      fill={colors[entry.sentiment as keyof typeof colors]}
                      className="transition-opacity hover:opacity-80"
                    />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: "rgba(24, 24, 27, 0.95)",
                    border: "1px solid rgba(63, 63, 70, 0.5)",
                    borderRadius: "8px",
                    fontSize: "12px",
                    color: "#fafafa",
                  }}
                  itemStyle={{color: "#fafafa"}}
                  formatter={(value, name) => [
                    `${value} mentions`,
                    isInteractive ? `${name} (click to explore)` : name,
                  ]}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Bar chart - timeline */}
          <div className="hidden h-56 flex-1 sm:block">
            <p className="mb-1 text-xs text-zinc-500 dark:text-zinc-400">Timeline</p>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chart.data} margin={{top: 5, right: 10, left: 0, bottom: 0}}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  className="stroke-zinc-200 dark:stroke-zinc-700"
                />
                <XAxis
                  dataKey="label"
                  tick={{fontSize: 9}}
                  className="text-zinc-500 dark:text-zinc-400"
                  tickLine={false}
                  axisLine={false}
                  interval="preserveStartEnd"
                />
                <YAxis
                  tick={{fontSize: 9}}
                  className="text-zinc-500 dark:text-zinc-400"
                  tickLine={false}
                  axisLine={false}
                  width={25}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "rgba(24, 24, 27, 0.95)",
                    border: "1px solid rgba(63, 63, 70, 0.5)",
                    borderRadius: "8px",
                    fontSize: "11px",
                  }}
                  labelStyle={{color: "#fafafa", fontWeight: 600}}
                  itemStyle={{color: "#a1a1aa"}}
                />
                <Bar
                  dataKey="positive"
                  stackId="sentiment"
                  fill={colors.positive}
                  name="Positive"
                />
                <Bar dataKey="neutral" stackId="sentiment" fill={colors.neutral} name="Neutral" />
                <Bar
                  dataKey="negative"
                  stackId="sentiment"
                  fill={colors.negative}
                  name="Negative"
                  radius={[2, 2, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      ) : (
        <div className="h-64 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chart.data} margin={{top: 10, right: 10, left: 0, bottom: 0}}>
              <CartesianGrid
                strokeDasharray="3 3"
                className="stroke-zinc-200 dark:stroke-zinc-700"
              />
              <XAxis
                dataKey="label"
                tick={{fontSize: 10}}
                className="text-zinc-500 dark:text-zinc-400"
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                tick={{fontSize: 10}}
                className="text-zinc-500 dark:text-zinc-400"
                tickLine={false}
                axisLine={false}
                width={30}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "rgba(24, 24, 27, 0.95)",
                  border: "1px solid rgba(63, 63, 70, 0.5)",
                  borderRadius: "8px",
                  fontSize: "12px",
                }}
              />
              <Bar dataKey="value" fill="#3b82f6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
